import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { environment } from '@hakimo-ui/hakimo/environments';
import { AuthUtils } from '@hakimo-ui/hakimo/types';
import { useLocalStorage } from '@hakimo-ui/hakimo/util';
import * as Sentry from '@sentry/react';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Auth0ProviderWithRedirectCallback from './Auth0ProviderWithRedirectCallback';
import AuthUtilsProvider from './AuthUtilsProvider';
import { listenForLogoutEvents, sendLogoutEvent } from './authWorker';
import { hasAuth0Password } from './passwordReset';

interface Props {
  children: ReactNode;
}

export const AuthProvider = (props: Props) => {
  const { children } = props;
  const [, , removeToken, getCurrent] = useLocalStorage<string>('token');
  const navigate = useNavigate();

  const localAuthUtils: AuthUtils = {
    getAccessToken: () => {
      const token = getCurrent();
      return token ? Promise.resolve(token) : Promise.reject();
    },
    logout: () => {
      removeToken();
      navigate('/login');
    },
  };

  return environment.authMode === 'auth0' ? (
    <Auth0ProviderWithRedirectCallback>
      <Auth0Auth>{children}</Auth0Auth>
    </Auth0ProviderWithRedirectCallback>
  ) : (
    <AuthUtilsProvider authUtils={localAuthUtils}>{children}</AuthUtilsProvider>
  );
};

function getStoredRefreshToken(): string | null {
  const cacheKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith('@@auth0spajs@@')
  );
  for (const key of cacheKeys) {
    try {
      const entry = JSON.parse(localStorage.getItem(key) ?? '');
      const refreshToken = entry?.body?.refresh_token;
      if (refreshToken) return refreshToken;
    } catch (error) {
      Sentry.captureException(error);
    }
  }
  return null;
}

async function revokeRefreshToken() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return;
  try {
    await fetch(`https://${environment.auth0.domain}/oauth/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: environment.auth0.clientId,
        token: refreshToken,
      }),
    });
  } catch (error) {
    Sentry.captureException(error);
  }
}

function Auth0Auth({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, logout, user } = useAuth0();

  useEffect(() => {
    listenForLogoutEvents(logout);
  }, [logout]);

  const logoutAndPublish = useCallback(
    async (options?: LogoutOptions) => {
      await revokeRefreshToken();
      logout(options);
      sendLogoutEvent();
    },
    [logout]
  );

  const getAccessToken = useCallback(
    async (ignoreCache?: boolean) => {
      const token = await getAccessTokenSilently({
        ignoreCache: ignoreCache ?? false,
      });
      return token;
    },
    [getAccessTokenSilently]
  );

  const canResetPassword = hasAuth0Password(user);

  const authUtils = useMemo(
    () => ({
      getAccessToken,
      logout: logoutAndPublish,
      profilePic: user?.picture,
      canResetPassword,
    }),
    [getAccessToken, logoutAndPublish, user?.picture, canResetPassword]
  );

  return (
    <AuthUtilsProvider authUtils={authUtils}>{children}</AuthUtilsProvider>
  );
}
